import {SortableContext, horizontalListSortingStrategy} from '@dnd-kit/sortable';
import {match} from 'ts-pattern';

import {useMemo} from 'react';

import {
  GROUP_HEADER_ROW_HEIGHT,
  HEADER_CELL_BORDER_BOTTOM,
  THEAD_BORDER,
} from '../../constants/borderStyles';
import {SELECTION_COLUMN_WIDTH} from '../../constants/selectionColumn';
import {EagColumn} from '../../types/EagColumn';
import {EagColumnOrGroup} from '../../types/EagColumnGroup';
import {EagColumnState} from '../../types/EagColumnState';
import {isEagColumnGroup} from '../../utils/isEagColumnGroup';
import {ActionHeaderCell} from './components/ActionHeaderCell/ActionHeaderCell';
import {GroupHeaderRow} from './components/GroupHeaderRow/GroupHeaderRow';
import {HeaderCell} from './components/HeaderCell/HeaderCell';
import {SelectAllCheckbox} from './components/SelectAllCheckbox/SelectAllCheckbox';

// DndContext is intentionally absent here — it lives in EagGridTable (the parent),
// wrapping the outer <div> instead of <table>. This prevents dnd-kit accessibility
// <div>s from being injected as invalid children of <table>.
//
// Drag state (activeDragId, overColumnId, dropSide) is read directly from the
// EagGridStore by HeaderCell — no prop drilling through this layer.
//
// Column menu state (showColumnMenu, showPinning, showAutosize, filterState,
// and all callbacks) is provided via ColumnMenuContext by EagGridTable —
// no prop drilling through this layer either.

export type HeaderRowProps = {
  columns: EagColumn[];
  columnStates: EagColumnState[];
  onSort: (key: string) => void;
  onResizeStart: (colKey: string, startX: number, colIndex: number) => void;
  sortableIds: string[];
  pinnedLeftOffsets: Map<string, number>;
  pinnedRightOffsets: Map<string, number>;
  columnConfig: EagColumnOrGroup[];
  columnGroupsVisible: boolean;
  // Pinning edge keys — used to compute isLastPinnedLeft / isFirstPinnedRight per cell
  lastPinnedLeftKey: string | null;
  firstPinnedRightKey: string | null;
  // Action column props — only present when action column is enabled
  actionColumnWidth?: number;
  headerHeightPx?: number;
  // Maps column key → <col> DOM index in colgroup. Accounts for filler position.
  colIndexMap: Map<string, number>;
  // Selection mode — controls whether to show SelectAllCheckbox or empty <th>
  rowSelectMode: 'SINGLE' | 'MULTIPLE' | 'NONE';
};

const hasAnyGroup = (config: EagColumnOrGroup[]): boolean => config.some(isEagColumnGroup);

export function HeaderRow(props: HeaderRowProps) {
  /**
   * @mandatory O(1) column state Map used in every header cell render.
   * new Map() from array is not guaranteed referentially stable by React Compiler.
   */
  const columnStateMap = useMemo(
    () => new Map(props.columnStates.map((cs) => [cs.key, cs])),
    [props.columnStates]
  );

  const visibleKeys = new Set(props.columns.map((col) => col.key));

  const showGroupRow = props.columnGroupsVisible && hasAnyGroup(props.columnConfig);

  const nonRightPinnedColumns = props.columns.filter((col) => {
    const cs = columnStateMap.get(col.key);
    return cs?.pinned !== 'RIGHT';
  });

  const rightPinnedColumns = props.columns.filter((col) => {
    const cs = columnStateMap.get(col.key);
    return cs?.pinned === 'RIGHT';
  });

  // Number of non-right-pinned visible columns — used for filler <th> positioning in group header
  const nonRightPinnedCount = nonRightPinnedColumns.length;

  // When the group header row is visible, the main header row cells must use
  // top: GROUP_HEADER_ROW_HEIGHT so they stick directly below the group row
  // instead of overlapping it at top: 0.
  const stickyTop = showGroupRow ? GROUP_HEADER_ROW_HEIGHT : 0;

  return (
    <SortableContext items={props.sortableIds} strategy={horizontalListSortingStrategy}>
      <thead style={THEAD_BORDER}>
        {showGroupRow && (
          <GroupHeaderRow
            columnConfig={props.columnConfig}
            visibleKeys={visibleKeys}
            actionColumnWidth={props.actionColumnWidth}
            nonRightPinnedCount={nonRightPinnedCount}
          />
        )}
        <tr>
          {/* checkbox pseudo-column header: SelectAllCheckbox for MULTIPLE, empty <th> for SINGLE */}
          {match(props.rowSelectMode)
            .with('MULTIPLE', () => <SelectAllCheckbox stickyTop={stickyTop} />)
            .with('SINGLE', () => (
              <th
                className="p-0 bg-white"
                style={{
                  width: SELECTION_COLUMN_WIDTH,
                  position: 'sticky',
                  top: stickyTop,
                  left: 0,
                  zIndex: 'var(--z-content-loader)',
                  ...HEADER_CELL_BORDER_BOTTOM,
                }}
              />
            ))
            .with('NONE', () => null)
            .exhaustive()}
          {/* left-pinned + unpinned header cells */}
          {nonRightPinnedColumns.map((column) => {
            const columnState = columnStateMap.get(column.key);
            if (!columnState) {
              return null;
            }
            return (
              <HeaderCell
                key={column.key}
                column={column}
                columnState={columnState}
                colIndex={props.colIndexMap.get(column.key) ?? 0}
                onSort={props.onSort}
                onResizeStart={props.onResizeStart}
                pinnedLeftOffset={props.pinnedLeftOffsets.get(column.key)}
                pinnedRightOffset={props.pinnedRightOffsets.get(column.key)}
                isLastPinnedLeft={props.lastPinnedLeftKey === column.key}
                isFirstPinnedRight={props.firstPinnedRightKey === column.key}
                stickyTop={stickyTop}
              />
            );
          })}
          {/* filler th: pairs with filler <col /> to absorb remaining table width.
              Positioned between unpinned and right-pinned so pinned columns sit at the right edge. */}
          <th
            aria-hidden
            style={{
              position: 'sticky',
              top: stickyTop,
              background: 'white',
              ...HEADER_CELL_BORDER_BOTTOM,
            }}
          />
          {/* right-pinned header cells */}
          {rightPinnedColumns.map((column) => {
            const columnState = columnStateMap.get(column.key);
            if (!columnState) {
              return null;
            }
            return (
              <HeaderCell
                key={column.key}
                column={column}
                columnState={columnState}
                colIndex={props.colIndexMap.get(column.key) ?? 0}
                onSort={props.onSort}
                onResizeStart={props.onResizeStart}
                pinnedLeftOffset={props.pinnedLeftOffsets.get(column.key)}
                pinnedRightOffset={props.pinnedRightOffsets.get(column.key)}
                isLastPinnedLeft={props.lastPinnedLeftKey === column.key}
                isFirstPinnedRight={props.firstPinnedRightKey === column.key}
                stickyTop={stickyTop}
              />
            );
          })}
          {/* action header cell — always at the far right */}
          {props.actionColumnWidth ? (
            <ActionHeaderCell
              actionColumnWidth={props.actionColumnWidth}
              heightPx={props.headerHeightPx ?? 40}
              stickyTop={stickyTop}
            />
          ) : null}
        </tr>
      </thead>
    </SortableContext>
  );
}
